'use client'

import { useCallback, useRef, useState } from 'react'
import { Box, Text, VStack } from '@chakra-ui/react'
import { FileUp, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface FileDropzoneProps {
  value?: File
  onChange: (file: File | undefined) => void
  invalid?: boolean
}

export function FileDropzone({ value, onChange, invalid }: FileDropzoneProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleFiles = useCallback(
    (files: FileList | null) => {
      const file = files?.[0]
      if (file) onChange(file)
    },
    [onChange],
  )

  return (
    <VStack gap={2} align="stretch" w={'100%'}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            inputRef.current?.click()
          }
        }}
        onDragOver={(event) => {
          event.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={(event) => {
          event.preventDefault()
          setIsDragging(false)
        }}
        onDrop={(event) => {
          event.preventDefault()
          setIsDragging(false)
          handleFiles(event.dataTransfer.files)
        }}
        borderWidth="1px"
        borderStyle="dashed"
        borderColor={invalid ? 'red.500' : isDragging ? 'primary.fg' : 'border'}
        borderRadius="lg"
        bg={isDragging ? 'bg.subtle' : 'bg.surface'}
        py={8}
        px={4}
        cursor="pointer"
        transition="all 0.15s"
        _hover={{
          borderColor: invalid ? 'red.500' : 'primary.fg',
          bg: 'bg.subtle',
        }}
      >
        <VStack gap={2}>
          <Box color={invalid ? 'red.500' : 'fg.muted'}>
            <FileUp size={28} />
          </Box>
          <Text fontSize="sm" fontWeight="medium" textAlign="center">
            {t('dashboard.override_deferral.dropzone_title')}
          </Text>
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            {t('dashboard.override_deferral.dropzone_hint')}
          </Text>
        </VStack>
      </Box>

      <input
        ref={inputRef}
        type="file"
        hidden
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={(event) => handleFiles(event.target.files)}
      />

      {value && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          px={3}
          py={2}
          borderWidth="1px"
          borderColor="border"
          borderRadius="md"
          bg="bg.subtle"
        >
          <Text fontSize="sm" truncate>
            {value.name}
          </Text>
          <Box
            as="button"
            color="fg.muted"
            _hover={{ color: 'fg' }}
            onClick={(event) => {
              event.stopPropagation()
              onChange(undefined)
              if (inputRef.current) inputRef.current.value = ''
            }}
          >
            <X size={16} />
          </Box>
        </Box>
      )}
    </VStack>
  )
}
